import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { LogOut, Menu } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Wordmark } from "@/components/shared/wordmark";
import { InstallInstructionsDialog, InstallMenuItem } from "@/components/pwa/install-menu-item";
import type { InstallMethod } from "@/hooks/use-install-prompt";
import { useAuth } from "@/providers/auth.provider";
import { NAVIGATION_ITEMS } from "../data/navigation";

const linkBase = "text-[14px] transition-colors";
const inactiveLink = "text-muted-foreground hover:text-foreground";
const activeLink = "text-brand font-semibold";

export function Masthead() {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [installHelp, setInstallHelp] = useState<InstallMethod | null>(null);

  return (
    <header className="sticky top-0 z-30 border-b-2 border-brand bg-background pt-[env(safe-area-inset-top)]">
      <div className="mx-auto flex h-14 w-full max-w-[1280px] items-center gap-4 sm:gap-6 px-4 sm:px-7">
        <Link to="/dashboard" aria-label="Home">
          <Wordmark />
        </Link>

        <nav aria-label="Primary" className="hidden items-center gap-[18px] md:flex">
          {NAVIGATION_ITEMS.map((item) => (
            <Link
              key={item.url}
              to={item.url}
              className={linkBase}
              activeProps={{ className: activeLink }}
              inactiveProps={{ className: inactiveLink }}
            >
              {item.title}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-[13px] text-muted-foreground" aria-label="Account menu">
                  {user.name.split(" ")[0]}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="text-[13px] font-medium">{user.name}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <InstallMenuItem onShowInstructions={setInstallHelp} />
                <DropdownMenuItem onClick={() => logout()}>
                  <LogOut aria-hidden /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open navigation">
                <Menu aria-hidden />
              </Button>
            </SheetTrigger>
            <SheetContent side="top" className="pt-10">
              <SheetHeader className="sr-only">
                <SheetTitle>Navigation</SheetTitle>
              </SheetHeader>
              <nav aria-label="Primary" className="flex flex-col divide-y divide-border">
                {NAVIGATION_ITEMS.map((item) => (
                  <Link
                    key={item.url}
                    to={item.url}
                    onClick={() => setMenuOpen(false)}
                    className="py-3 text-[15px]"
                    activeProps={{ className: "text-brand font-semibold" }}
                    inactiveProps={{ className: "text-foreground" }}
                  >
                    {item.title}
                  </Link>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <InstallInstructionsDialog method={installHelp} onClose={() => setInstallHelp(null)} />
    </header>
  );
}
